/**
 * Sales — the office sector's own calls (2026-10-13).
 *
 * Everything else this section needs comes from `standardSalesApi`, which hits
 * the same `/api/standard-sales` routes. These two are the ones Sales adds:
 * how an enquiry ended, and what you have learnt about it along the way.
 */

import { apiFetch } from './api';

export type SalesOutcome = 'won' | 'lost';

/**
 * Write a line against an enquiry — a call that went unanswered, how many
 * staff they have, when to try again.
 *
 * APPENDS, never replaces. The server stamps the date and keeps what was
 * already there, so the notes read as a log rather than one editable box that
 * the next person overwrites.
 */
export async function addEnquiryNote(
  id: string,
  note: string,
): Promise<{ id: string; notes: string | null }> {
  return apiFetch(`/api/sales/enquiries/${id}/note`, {
    method: 'PATCH',
    body: { note },
  });
}

/** Mark how an enquiry ended. The note is optional and also appended. */
/**
 * How an enquiry ended, and — when it was won — who is setting them up.
 *
 * `setUpBy` has been on the route and in the service since the handover was
 * built; only this call and the screen above it never passed it, so winning
 * from an enquiry always fell to the default. See the ask in
 * SalesEnquiryModal for what that cost.
 */
export async function markEnquiryOutcome(
  id: string,
  outcome: SalesOutcome,
  note?: string,
  setUpBy?: 'us' | 'them',
): Promise<{ id: string; status: string; notes: string | null }> {
  return apiFetch(`/api/sales/enquiries/${id}/outcome`, {
    method: 'PATCH',
    body: {
      outcome,
      ...(note && note.trim() !== '' ? { note: note.trim() } : {}),
      ...(setUpBy ? { setUpBy } : {}),
    },
  });
}

/**
 * THE ONBOARDING WORKLIST — who has said yes and is not set up yet.
 *
 * Reagan, 2026-10-13: *"i just want sales people to deal with them then i can
 * log on and see who i need to sort tennants out for"*. Nothing here creates
 * anything; it reads a queue and lets the person who does the setting up tick
 * a company off it.
 */
export interface OnboardingRow {
  id: string;
  reference: string;
  companyName: string;
  contactName: string;
  contactEmail: string | null;
  contactPhone: string | null;
  siteLocation: string | null;
  category: string;
  description: string;
  notes: string | null;
  assignedToName: string | null;
  assignedToId: string | null;
  createdAt: string;
  onboardedAt: string | null;
  onboardedByName: string | null;
}

export interface OnboardingList {
  waiting: OnboardingRow[];
  done: OnboardingRow[];
}

export async function fetchOnboarding(signal?: AbortSignal): Promise<OnboardingList> {
  return apiFetch('/api/sales/onboarding', signal ? { signal } : {});
}

/** The operator's own price list, read only. Any other company gets a 404. */
export interface SalesPriceList {
  countryCode: string;
  currency: { code: string; symbol: string; decimalPlaces: number };
  /** `monthlyPence` null is the quoted band. */
  bands: Array<{ minEmployees: number; maxEmployees: number | null; monthlyPence: number | null }>;
  addons: Array<{ key: string; name: string; monthlyPence: number; onlyForSectors: string[] }>;
  offers: Array<{
    code: string;
    percentOff: number | null;
    months: number | null;
    freeDays: number | null;
    lastUseDate: string;
    onlyForSectors: string[];
  }>;
}

export async function fetchSalesPriceList(signal?: AbortSignal): Promise<SalesPriceList> {
  return apiFetch('/api/sales/price-list', signal ? { signal } : {});
}

/** The list price per month for a number of staff; `quoted` is the open band. Operator company only. */
export type HeadcountPrice =
  | { headcount: number; quoted: false; minEmployees: number; maxEmployees: number | null; monthlyPence: number }
  | { headcount: number; quoted: true };

export async function fetchHeadcountPrice(headcount: number, signal?: AbortSignal): Promise<HeadcountPrice> {
  return apiFetch(`/api/sales/quotes/headcount-price?headcount=${headcount}`, signal ? { signal } : {});
}

/** Tick a company off the list, or (with false) put it back on. */
export async function setOnboarded(
  id: string,
  onboarded: boolean,
): Promise<{ id: string; onboardedAt: string | null }> {
  return apiFetch(`/api/sales/enquiries/${id}/onboarded`, {
    method: 'PATCH',
    body: { onboarded },
  });
}

/**
 * THE SALES TEAM TICK — keyed by employee, written to their login.
 *
 * Reagan: *"a box to tick in the employees folder ... to add to sales team"*.
 * The box is on the employee record; the column is on the user behind them,
 * because an enquiry is assigned to a login. `reason` is what lets the screen
 * say "they have no account yet" instead of drawing a tick that does nothing.
 */
export interface SalesTeamMembership {
  userId: string | null;
  inSalesTeam: boolean;
  reason: 'ok' | 'no_account' | 'inactive';
}

export async function fetchSalesTeamMembership(
  employeeId: string,
  signal?: AbortSignal,
): Promise<SalesTeamMembership> {
  return apiFetch(`/api/sales/team/employee/${employeeId}`, signal ? { signal } : {});
}

export async function setSalesTeamMembership(
  employeeId: string,
  inTeam: boolean,
): Promise<SalesTeamMembership> {
  return apiFetch(`/api/sales/team/employee/${employeeId}`, {
    method: 'PATCH',
    body: { inTeam },
  });
}

/**
 * HOW MANY COMPANIES ARE WAITING TO BE SET UP — the sidebar badge.
 *
 * A COUNT, not the list. The sidebar asks every sixty seconds, for every
 * signed-in user, on every page; fetching the queue itself would carry every
 * name, email and phone number on it to a screen that renders one digit.
 */
export async function fetchOnboardingBadge(signal?: AbortSignal): Promise<number> {
  const data = await apiFetch<{ count: number }>(
    '/api/sales/onboarding/badge',
    signal ? { signal } : {},
  );
  return data.count;
}
